import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useHasRole, useRender, useRenders, useRerender } from '../api/hooks';
import type { RenderDetail } from '../api/types';
import { PageHeader } from '../app/Shell';
import { TIER_LABEL } from '../app/labels';
import { Badge, Button, Card, Checkbox, ConfirmDialog, Drawer, Empty, ErrorNote, Icon, Id, JsonView, KeyValues, QueryView, ReasonField, Section, SuccessNote, When, fmtDateTime, fmtUsd, reasonError, toast } from '../ui';
import s from './Manga.module.css';
import { LoadMore } from './parts';

const QUALITY = { approved: { label: '通過', tone: 'ok' }, needs_review: { label: '待檢查', tone: 'warn' }, rejected: { label: '退回', tone: 'danger' } } as const;

/** Every manga render: its tier, what it cost, which pages the image model drew, and the failures. */
export function MangaPage() {
  const [params, setParams] = useSearchParams();
  const [tier, setTier] = useState<'' | 'basic' | 'illustrated'>('');
  const [partial, setPartial] = useState(false);
  const list = useRenders({ tier: tier || undefined, partial: partial || undefined });
  const open = params.get('render');
  const select = (id: string | null) => setParams(id ? { render: id } : {}, { replace: true });

  return (
    <>
      <PageHeader title="漫畫" description="最近的漫畫繪製：版本、成本、AI 畫了多少頁、哪些頁改用合成，以及失敗的繪製。" />
      {list.failedJobs.length > 0 && (
        <div className="callout" data-tone="danger">
          <span className="callIcon">✕</span>
          <div className="callBody stack" style={{ gap: 6 }}>
            <strong>最近 {list.failedJobs.length} 個繪製永久失敗</strong>
            {list.failedJobs.slice(0, 3).map((j) => (
              <div key={j._id} className="row small">
                <When at={j.updatedAt} />
                <span className="truncate" style={{ flex: 1, minWidth: 0 }} title={j.error ?? ''}>
                  {j.error ?? '—'}
                </span>
                <Link to={`/jobs?job=${j._id}`}>詳情 →</Link>
              </div>
            ))}
          </div>
        </div>
      )}
      <Card flush>
        <div className="filters">
          <label>
            版本
            <select value={tier} onChange={(e) => setTier(e.target.value as typeof tier)}>
              <option value="">全部</option>
              <option value="basic">{TIER_LABEL.basic}</option>
              <option value="illustrated">{TIER_LABEL.illustrated}</option>
            </select>
          </label>
          <Checkbox checked={partial} onChange={setPartial}>
            只看有頁面改用合成的
          </Checkbox>
        </div>
        <QueryView query={list} loading="載入繪製記錄…">
          {() =>
            list.rows.length ? (
              <>
                <div className={s.grid}>
                  {list.rows.map((r) => (
                    <button key={r._id} type="button" className={s.tile} aria-current={r._id === open || undefined} onClick={() => select(r._id)}>
                      <div className={s.thumb} style={list.thumbs[r._id] ? { backgroundImage: `url("${list.thumbs[r._id]}")` } : undefined}>
                        {!list.thumbs[r._id] && <Icon name="image" size={32} />}
                        <span className={s.tier} data-tier={r.tier}>
                          {TIER_LABEL[r.tier]}
                        </span>
                        {r.qualityStatus !== 'approved' && (
                          <span className={s.quality}>
                            <Badge tone={QUALITY[r.qualityStatus].tone}>{QUALITY[r.qualityStatus].label}</Badge>
                          </span>
                        )}
                      </div>
                      <div className={s.tileBody}>
                        <strong className={s.tileTitle} title={r.title ?? ''}>
                          {r.title ?? '（無標題）'}
                        </strong>
                        <div className={s.tileMeta}>
                          <span>
                            v{r.renderVersion}
                            {!r.current && ' · 舊版'}
                          </span>
                          <When at={r.createdAt} />
                        </div>
                        <div className={s.tileMeta}>
                          <span>{r.illustration ? `AI 畫 ${r.illustration.pagesDrawn}/${r.illustration.pagesTotal} 頁` : '合成頁面'}</span>
                          <span className="num">{fmtUsd(r.costMicrousd / 1e6)}</span>
                        </div>
                        {!!r.illustration?.composedPages.length && <Badge tone="warn">第 {r.illustration.composedPages.join('、')} 頁改用合成</Badge>}
                      </div>
                    </button>
                  ))}
                </div>
                <LoadMore q={list} />
              </>
            ) : (
              <Empty hint="換一個篩選；玩家玩完一章後漫畫會自動繪製。">沒有符合的繪製。</Empty>
            )
          }
        </QueryView>
      </Card>
      <Drawer open={!!open} title="漫畫繪製" onClose={() => select(null)}>
        {open && <RenderView id={open} />}
      </Drawer>
    </>
  );
}

function RenderView({ id }: { id: string }) {
  const q = useRender(id);
  return <QueryView query={q} loading="載入頁面…">{(r) => <RenderBody r={r} />}</QueryView>;
}

function RenderBody({ r }: { r: RenderDetail }) {
  const canRerender = useHasRole('support');
  const [confirm, setConfirm] = useState(false);
  const [reuse, setReuse] = useState(true);
  const [reason, setReason] = useState('');
  const rerender = useRerender();
  // An illustrated page is one panel per page drawn with its words; show each page once.
  const pages = r.tier === 'illustrated' ? r.panels.filter((p, i, all) => p.page === null || all.findIndex((x) => x.page === p.page) === i) : r.panels;

  return (
    <>
      <KeyValues
        items={[
          ['章節', r.chapter?.title ?? <Id value={r.chapterId} />],
          ['玩家', r.chapter?.ownerUserIds.length ? r.chapter.ownerUserIds.map((u) => <Link key={u} to={`/users/${u}`} style={{ marginRight: 8 }}>{u.slice(0, 8)}</Link>) : '—'],
          ['版本', `${TIER_LABEL[r.tier]} · v${r.renderVersion} · ${r.locale}${r.chapter?.currentRenderId === r._id ? '（現用）' : ''}`],
          ['AI 畫 / 總頁', r.illustration ? `${r.illustration.pagesDrawn} / ${r.illustration.pagesTotal}${r.illustration.previewPages ? `（試閱 ${r.illustration.previewPages} 頁）` : ''}` : '—'],
          ['合成頁', r.illustration?.composedPages.join(', ') || '—'],
          ['收取章節券', r.illustration?.creditsCharged ?? '—'],
          ['成本', fmtUsd(r.costMicrousd / 1e6)],
          ['修復自', r.repairOf ? <Id value={r.repairOf} /> : '—'],
          ['繪製時間', fmtDateTime(r.createdAt)],
        ]}
      />
      <Section title="重新繪製">
        {rerender.isSuccess ? (
          <SuccessNote>
            已排隊繪製 v{rerender.data.renderVersion}（job <Link to={`/jobs?job=${rerender.data.jobId}`}>{rerender.data.jobId.slice(0, 8)}</Link>），不向玩家收費。
          </SuccessNote>
        ) : (
          <Button variant="primary" disabled={!canRerender} title={canRerender ? undefined : '需要客服角色'} onClick={() => setConfirm(true)}>
            以同一版本重新繪製
          </Button>
        )}
      </Section>
      <Section title={`頁面（${pages.length}）`}>
        {pages.length ? (
          <div className="pages">
            {pages.map((p) => (
              <figure key={p.panelId}>
                <a href={p.url} target="_blank" rel="noreferrer">
                  <img src={p.url} alt={`第 ${p.page ?? p.index + 1} ${r.tier === 'illustrated' ? '頁' : '格'}`} loading="lazy" />
                </a>
                <figcaption>
                  {r.tier === 'illustrated' ? `第 ${p.page ?? p.index + 1} 頁` : `第 ${p.index + 1} 格`} · {p.lettering === 'in_art' ? 'AI 繪製' : '合成'}
                  {p.status !== 'ready' && <> · <Badge tone={p.status === 'failed' ? 'danger' : 'warn'}>{p.status}</Badge></>}
                </figcaption>
              </figure>
            ))}
          </div>
        ) : (
          <p className="faint">這次繪製沒有頁面。</p>
        )}
      </Section>
      <Section title="分鏡 (storyboard)">
        <JsonView value={r.storyboard} />
      </Section>
      <ConfirmDialog
        open={confirm}
        title="重新繪製這一章？"
        confirmLabel="重新繪製"
        busy={rerender.isPending}
        canConfirm={!reasonError(reason)}
        onCancel={() => setConfirm(false)}
        onConfirm={() => rerender.mutate({ chapterId: r.chapterId, reuseDrawnPages: reuse, reason: reason.trim() }, { onSuccess: (x) => { setConfirm(false); toast(`已排隊繪製 v${x.renderVersion}，不向玩家收費`); } })}
        error={rerender.isError ? <ErrorNote error={rerender.error} /> : null}
      >
        <p className="muted small">
          以{TIER_LABEL[r.tier]}繪製一個新版本，玩家完成後會看到新版本。不收章節券，也不佔玩家的免費修復次數；{r.tier === 'illustrated' ? '繪製版會用到圖像 AI 花費。' : '基本版幾乎沒有成本。'}
        </p>
        {r.tier === 'illustrated' && (
          <Checkbox checked={reuse} onChange={setReuse}>
            保留已由 AI 畫好的頁面，只重畫合成頁
          </Checkbox>
        )}
        <ReasonField value={reason} onChange={setReason} autoFocus />
      </ConfirmDialog>
    </>
  );
}
